import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { portalDs, PortalDsBadge, PortalDsButton, PortalDsInput } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import AvatarUpload from 'src/components/form-new/avatar-upload';
import notificationService from 'src/services/notification/notification.service';
import { useUpdateClientMutation } from 'src/pages/client-management/endpoints/client.endpoints';
import { useUploadImageMutation } from 'src/redux/survey/survey.endpoints';
import { ClientAvatar } from './client-avatar';
import { StudioMigratedBadge } from './studio-migrated-badge';
import { getClientStatus, getWebsiteLabel, isClientStudioMigrated } from '../../utils/client-display.utils';
import { isValidClientWebsite, normalizeClientWebsite } from '../../utils/client-url.utils';

const ManageClientDrawerHeader = () => {
  const { activeClient, setClient } = useActiveClient();
  const dispatch = useDispatch();
  const [updateClient] = useUpdateClientMutation();
  const [uploadImage] = useUploadImageMutation();
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(activeClient?.brand ?? null);
  const [addingWebsite, setAddingWebsite] = useState(false);
  const [websiteDraft, setWebsiteDraft] = useState('');
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [isSavingWebsite, setIsSavingWebsite] = useState(false);

  useEffect(() => {
    setLocalImageUrl(activeClient?.brand ?? null);
  }, [activeClient?.brand]);

  if (!activeClient) {
    return null;
  }

  const status = getClientStatus(activeClient);
  const hasWebsite = Boolean(activeClient.website?.trim());
  const showMissingWebsiteWarning = !hasWebsite && !isClientStudioMigrated(activeClient);

  const handleUpload = async(file: File) => {
    if (!activeClient.ext_client_id) {
      return;
    }
    if (file.size > 500 * 1024) {
      notificationService.handleError(null, dispatch, 'File size exceeds 500KB limit');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'client');
      formData.append('uuid', activeClient.ext_client_id);
      const response = await uploadImage({ formData, uuid: activeClient.ext_client_id }).unwrap();
      setLocalImageUrl(response.item.url);
      await updateClient({
        clientId: activeClient.ext_client_id,
        data: {
          ...activeClient,
          brand: response.item.url,
        },
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      notificationService.handleError(null, dispatch, 'Error uploading image. Please try again.');
    }
  };

  const startWebsiteEdit = () => {
    setWebsiteDraft(activeClient.website ? getWebsiteLabel(activeClient.website) : '');
    setWebsiteError(null);
    setAddingWebsite(true);
  };

  const handleSaveWebsite = async() => {
    const trimmed = websiteDraft.trim();
    if (!trimmed) {
      setWebsiteError(null);
      setAddingWebsite(false);
      return;
    }

    const normalized = normalizeClientWebsite(trimmed);
    if (!isValidClientWebsite(normalized)) {
      setWebsiteError('Enter a valid URL');
      return;
    }

    try {
      setIsSavingWebsite(true);
      const updated = await updateClient({
        clientId: activeClient.ext_client_id,
        data: {
          title: activeClient.title,
          website: normalized,
        },
      }).unwrap();
      setClient({
        ...activeClient,
        ...updated,
        website: updated?.website ?? normalized,
      });
      setWebsiteError(null);
      setAddingWebsite(false);
      setWebsiteDraft('');
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Error updating client website');
    } finally {
      setIsSavingWebsite(false);
    }
  };

  return (
    <div className="mb-8 flex flex-wrap items-center gap-4">
      {localImageUrl ? (
        <AvatarUpload
          value={localImageUrl}
          size={52}
          onChange={handleUpload}
        />
      ) : (
        <button
          type="button"
          className="rounded-full"
          aria-label="Upload client logo"
          onClick={() => document.getElementById('client-logo-upload')?.click()}
        >
          <ClientAvatar
            title={activeClient.title}
            brand={localImageUrl}
            size={52}
          />
          <input
            id="client-logo-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                void handleUpload(file);
              }
            }}
          />
        </button>
      )}

      <div className="min-w-0">
        <h1 className="text-[24px] font-bold tracking-[-0.5px] text-[#0D0F1A]">
          {activeClient.title}
        </h1>
        {hasWebsite && !addingWebsite ? (
          <button
            type="button"
            className={cn(portalDs.bodySm, 'mt-0.5 text-left hover:underline')}
            aria-label="Edit website"
            onClick={startWebsiteEdit}
          >
            {getWebsiteLabel(activeClient.website)}
          </button>
        ) : null}
        {!hasWebsite && !addingWebsite ? (
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            {showMissingWebsiteWarning ? (
              <span className="text-sm font-medium text-[#D97706]">
                {getWebsiteLabel(null)}
              </span>
            ) : null}
            <PortalDsButton
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={startWebsiteEdit}
            >
              + Add website
            </PortalDsButton>
          </div>
        ) : null}
        {addingWebsite ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <PortalDsInput
              autoFocus
              value={websiteDraft}
              placeholder="www.example.com"
              className="h-8 w-[220px]"
              onChange={(event) => {
                setWebsiteDraft(event.target.value);
                if (websiteError) {
                  setWebsiteError(null);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleSaveWebsite();
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setAddingWebsite(false);
                  setWebsiteDraft('');
                  setWebsiteError(null);
                }
              }}
            />
            <PortalDsButton
              variant="primary"
              size="sm"
              disabled={isSavingWebsite}
              onClick={() => void handleSaveWebsite()}
            >
              Save
            </PortalDsButton>
            <PortalDsButton
              variant="ghost"
              size="sm"
              onClick={() => {
                setAddingWebsite(false);
                setWebsiteDraft('');
                setWebsiteError(null);
              }}
            >
              Cancel
            </PortalDsButton>
            {websiteError ? (
              <p
                className="w-full text-xs text-[#DC2626]"
                role="alert"
              >
                {websiteError}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StudioMigratedBadge client={activeClient} />
        <PortalDsBadge
          pill
          variant={status.variant}
        >
          {status.label}
        </PortalDsBadge>
      </div>
    </div>
  );
};

export default ManageClientDrawerHeader;
